import { defer, Observable, throwError } from 'rxjs';

/**
 * Cached after the first time `navigator.locks.request` is observed to fail
 * with `NotAllowedError` / "request() is not allowed in this context" so we
 * skip the (expensive, error-throwing) call on every subsequent invocation.
 */
let lockManagerForbidden = false;

function isLockManagerAllowed(): boolean {
	return !lockManagerForbidden;
}

function isLockNotAllowedError(error: unknown): boolean {
	return (
		error instanceof DOMException
		&& (error.name === 'NotAllowedError' || error.name === 'SecurityError')
	)
		|| (error instanceof Error && (/not allowed in this context/iu).test(error.message));
}

/**
 * Error thrown when {@link withLock} is called with `ifAvailable: true` and
 * the lock is already held by another tab/window in the same browser profile.
 */
export class LockUnavailableError extends Error {

	constructor(public readonly lockName: string) {
		super(`Lock "${ lockName }" is already held by another tab`);

		this.name = 'LockUnavailableError';
	}

}

export interface ILockOptions {

	/**
	 * Lock mode. Defaults to `'exclusive'`.
	 * @see https://developer.mozilla.org/docs/Web/API/LockManager/request
	 */
	mode?: LockMode;

	/**
	 * When `true`, if the lock is already held the observable errors
	 * immediately with {@link LockUnavailableError} instead of waiting.
	 *
	 * Defaults to `false` — the observable awaits (queues) until the holder
	 * releases the lock, then emits. The browser auto-releases when the
	 * holding tab closes or crashes, so the queued tab takes over safely.
	 */
	ifAvailable?: boolean;
}

/**
 * Runs a work observable inside a Web Locks API lock scoped to the current
 * browser profile and origin. The lock is acquired BEFORE the work observable
 * is subscribed to, and released as soon as the work completes, errors, or
 * the consumer unsubscribes — whichever happens first.
 *
 * ```ts
 * withLock(`deposit:${checkoutSessionId}`, () => this.__http.post('/deposit', body))
 *   .pipe(map(dto => new Transaction(dto)));
 * ```
 *
 * If another tab in the same browser holds the lock, this subscription queues
 * until the holder releases — including on tab close/crash, which the browser
 * handles automatically. Pass `{ ifAvailable: true }` to fail fast with
 * {@link LockUnavailableError} instead of waiting.
 *
 * If the Web Locks API is unavailable (older browsers / non-secure contexts)
 * or forbidden in the current context (cross-origin / sandboxed iframe, where
 * the browser throws `NotAllowedError: request() is not allowed in this context`),
 * the work is run directly without locking — the helper never breaks the flow.
 */
export function withLock<T>(
	name: string,
	work: () => Observable<T>,
	options: ILockOptions = {},
): Observable<T> {
	const { mode = 'exclusive', ifAvailable = false } = options;

	return new Observable<T>(subscriber => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (typeof navigator === 'undefined' || !navigator.locks || !isLockManagerAllowed()) {
			// Web Locks API unavailable or forbidden in this context
			// (e.g. cross-origin / sandboxed iframe, insecure context) —
			// run the work directly without locking.
			return defer(work).subscribe(subscriber);
		}

		const abortController = new AbortController();
		let innerSubscription: { unsubscribe: () => void } | undefined;

		// Promise we control to hold the lock until the inner work tears down.
		// `navigator.locks.request` releases the lock when this resolves.
		let releaseLock!: () => void;
		const lockHeld = new Promise<void>(resolve => {
			releaseLock = resolve;
		});

		const requestOptions: LockOptions = ifAvailable
			? { mode, ifAvailable: true }
			: { mode, signal: abortController.signal };

		try {
			navigator.locks
				.request(name, requestOptions, async acquiredLock => {
					if (acquiredLock === null) {
						// The lock was not granted. Two distinct causes:
						//  - `ifAvailable: true` and the lock is currently held by
						//    another tab → genuine fail-fast LockUnavailableError.
						//  - the queued request was aborted via the signal during
						//    teardown (consumer unsubscribed before the lock was
						//    granted) → NOT an error, just bail out quietly.
						// Only surface LockUnavailableError for the fail-fast mode
						// and only while the subscriber is still active.
						if (ifAvailable && !subscriber.closed)
							subscriber.error(new LockUnavailableError(name));

						return;
					}

					if (subscriber.closed) {
						// Unsubscribed before the lock was granted — release immediately.
						return;
					}

					// Subscribe to the work INSIDE the lock callback so any
					// subscribe-time side effects (HTTP requests, etc.) happen
					// strictly while the lock is held. Release the lock as soon
					// as the work settles (complete/error) regardless of whether
					// the downstream consumer has unsubscribed yet.
					innerSubscription = defer(work).subscribe({
						next: (value: T) => void subscriber.next(value),
						error: (error: unknown) => {
							subscriber.error(error);

							releaseLock();
						},
						complete: () => {
							subscriber.complete();

							releaseLock();
						},
					});

					await lockHeld;
				})
				.catch((error: unknown) => {
					// Aborted via the signal during unsubscription — swallow.
					if (error instanceof DOMException && error.name === 'AbortError')
						return;

					// `request() is not allowed in this context` — thrown asynchronously
					// in some browsers (e.g. cross-origin / sandboxed iframe). Fall back
					// to running the work directly without locking instead of failing.
					if (isLockNotAllowedError(error)) {
						lockManagerForbidden = true;

						if (subscriber.closed)
							return;

						innerSubscription = defer(work).subscribe(subscriber);

						return;
					}

					subscriber.error(error);
				});
		} catch (error) {
			// Some browsers throw synchronously when `navigator.locks.request`
			// is called in a forbidden context — fall back to direct execution.
			if (isLockNotAllowedError(error)) {
				lockManagerForbidden = true;

				return defer(work).subscribe(subscriber);
			}

			throw error;
		}

		return () => {
			abortController.abort();

			innerSubscription?.unsubscribe();

			releaseLock();
		};
	});
}

/**
 * Helper to build a one-shot observable that errors with {@link LockUnavailableError}
 * without acquiring a lock.
 */
export function lockUnavailable$<T>(lockName: string): Observable<T> {
	return throwError(() => new LockUnavailableError(lockName));
}
